import { Switch } from "@mui/material";
import { CloseGreyIcon, CloseRedIcon } from "../../../assets/icons";
import { useEffect, useState } from "react";
import { WorkingHourSettingsDTO } from "../../../models/workingHours/workingHour.response";
import { CreateWorkingHourListDTO, DayOfWeek, UpdateWorkingHourListDTO } from "../../../models/workingHours/workingHour.request";
import { useNotification } from "../../toster-notification/NotificationContext";
import { workingHoursService } from "../../../services/workingHoursService";
import { number } from "framer-motion";
import Loader from "../../shared/Loader";


interface LocalWorkingHoursViewProps {
    localId: string;
    onClose: () => void;
}

export default function LocalWorkingHoursView({ localId, onClose }: LocalWorkingHoursViewProps) {

    const [workingHours, setWorkingHours] = useState<WorkingHourSettingsDTO[]>([]);

    const [loading, setLoading] = useState<boolean>(false);
    const { notify } = useNotification();

    useEffect(() => {
        fetchWorkingHours();
    }, [localId]);


    const fetchWorkingHours = async () => {
        setLoading(true);
        try {
            const response = await workingHoursService.getByLocalId(localId);

            if (response.success && response.data) {
                setWorkingHours(response.data.sort((a, b) => a.dayOfWeek - b.dayOfWeek));
            }
        } catch (error) {
            notify("error", "Greška prilikom učitavanja radnog vremena.");
        } finally {
            setLoading(false);
        }
    }

    const handleSave = async () => {
        if(workingHours.some(wh => !wh.openTime || !wh.closeTime)) {
            notify("error", "Molimo unesite vreme otvaranja i zatvaranja za sve dane.");
            return;
        }

        setLoading(true);
        try {
            const dto: UpdateWorkingHourListDTO = {
                workingHours: workingHours.map((wh) => ({
                    id: wh.id.startsWith("temp-") ? null : wh.id,
                    dayOfWeek: wh.dayOfWeek,
                    openTime: wh.openTime.length === 5 ? wh.openTime + ":00" : wh.openTime,
                    closeTime: wh.closeTime.length === 5 ? wh.closeTime + ":00" : wh.closeTime,
                    isClosed: wh.isClosed,
                    isOpen24Hours: wh.isOpen24Hours
                })),
            };

            const response = await workingHoursService.updateList(localId, dto);
            if (response.success) {
                notify("success", "Radno vreme je uspešno sačuvano.");
                onClose();
            } else {
                notify("error", "Greška prilikom čuvanja radnog vremena.");
            }
        } catch (error) {
            notify("error", "Greška prilikom čuvanja radnog vremena.");
        } finally {
            setLoading(false);
        }
    }

    const handleWorkingHourChange = <K extends keyof WorkingHourSettingsDTO>(id: string, field: K, value: WorkingHourSettingsDTO[K]) => {
        setWorkingHours(prev => (
            prev.map(wh => wh.id === id ? { ...wh, [field]: value } : wh)
        ))
    }

    const addWorkingHour = () => {
        const newWorkingHour: WorkingHourSettingsDTO = {
            id: `temp-${crypto.randomUUID()}`,
            openTime: "",
            closeTime: "",
            dayOfWeek: DayOfWeek.Monday,
            isClosed: false,
            isOpen24Hours: false,
        }

        setWorkingHours([...workingHours, newWorkingHour])
    }

    const removeWorkingHour = (id: string) => {
        setWorkingHours(workingHours.filter(wh => wh.id !== id));
    }


    if(loading) {
        return <Loader />;
    }

    return (
        <section className="modal-section working-hours">
            <div className="w-layout-blockcontainer container w-container">
                <div className="modal-wrapper">
                    <div className="modal-div">
                        <div className="modal-header-div">
                            <div>
                                <div className="modal-header">Upravljaj radnim vremenom lokala</div>
                                <div className="description-text">Unesite detalje o radnom vremenu</div>
                            </div>
                            <div className="modal-button-div" onClick={onClose}>
                                <img loading="lazy" src={CloseGreyIcon} alt="" className="icon-size-20 close" />
                            </div>
                        </div>
                        <div className="modal-form-block w-form">
                            <form className="modal-form">

                                {workingHours.map((wh) => (
                                    <div className="modal-form-div" key={wh.id}>
                                        <label className="modal-form-label">Radno vreme</label>
                                        <div className="div-block-109">
                                            <div className="div-block-110">
                                                <select className="form-select strech w-select"
                                                    value={wh.dayOfWeek}
                                                    onChange={(e) => handleWorkingHourChange(wh.id, "dayOfWeek", Number(e.target.value))}
                                                >
                                                    <option value={DayOfWeek.Monday}>Ponedeljak</option>
                                                    <option value={DayOfWeek.Tuesday}>Utorak</option>
                                                    <option value={DayOfWeek.Wednesday}>Sreda</option>
                                                    <option value={DayOfWeek.Thursday}>Četvrtak</option>
                                                    <option value={DayOfWeek.Friday}>Petak</option>
                                                    <option value={DayOfWeek.Saturday}>Subota</option>
                                                    <option value={DayOfWeek.Sunday}>Nedelja</option>
                                                </select>
                                                <input className="form-input grey w-input" type="time"
                                                    value={wh.openTime}
                                                    onChange={(e) => handleWorkingHourChange(wh.id, "openTime", e.target.value)}
                                                />
                                                <input className="form-input grey w-input" type="time"
                                                    value={wh.closeTime}
                                                    onChange={(e) => handleWorkingHourChange(wh.id, "closeTime", e.target.value)}
                                                />
                                            </div>
                                            <div className="div-block-110">
                                                <div className="div-block-112" onClick={() => removeWorkingHour(wh.id)}>
                                                    <img src={CloseRedIcon} loading="lazy" alt="" className="icon-size-20" />
                                                </div>
                                                <div className="div-block-111">
                                                    <div>Zatvoreno</div>
                                                    <Switch
                                                        checked={wh.isClosed}
                                                        onChange={(e) => handleWorkingHourChange(wh.id, "isClosed", e.target.checked)}
                                                    />
                                                </div>
                                                <div className="div-block-111">
                                                    <div>Otvoreno 24/7</div>
                                                    <Switch
                                                        checked={wh.isOpen24Hours}
                                                        onChange={(e) => handleWorkingHourChange(wh.id, "isOpen24Hours", e.target.checked)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <div className="small-box-div green-bg pointer" onClick={addWorkingHour}>
                                    <div>Dodaj nove radne časove</div>
                                </div>
                            </form>
                        </div>
                        <div className="modal-buttons">
                            <div className="button-secondary medium" onClick={onClose}>
                                <div>Otkaži</div>
                            </div>
                            <div className="button-primary medium" onClick={handleSave}>
                                <div>Sačuvaj radno vreme</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

    );
}